import Accordion from '@/common/components/Accordion'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import SelectController from '@/common/components/SelectController'
import { formatCurrency } from '@/common/utils/formatters'
import { options as billByOptions } from '@/features/privateCompanyLeadsFunnel/constants/billByOptions.constant'
import { options as billGroupingModeOptions } from '@/features/privateCompanyLeadsFunnel/constants/billGroupingModeOptions.constant'
import { options as cycleOptions } from '@/features/privateCompanyLeadsFunnel/constants/cycleOptions.constant'
import { options as durationInMonthsOptions } from '@/features/privateCompanyLeadsFunnel/constants/durationInMonthsOptions.constant'
import { options as expectedPaymentModalityOptions } from '@/features/privateCompanyLeadsFunnel/constants/expectedPaymentModalityOptions.constant'
import { options as fiscalDocumentRequirement } from '@/features/privateCompanyLeadsFunnel/constants/fiscalDocumentRequirement.constant'
import { options as fiscalDocumentConsumerOptions } from '@/features/privateCompanyLeadsFunnel/constants/fiscalDocumentConsumer.constant'
import { options as monthDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/monthDaysOptions.constant'
import { options as paymentMethodOptions } from '@/features/privateCompanyLeadsFunnel/constants/paymentMethodOptions.constant'
import { options as weekDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/weekDaysOptions.constants'
import { options as defaultContractCnpjOptions } from "@/features/publicCompanyLeadsFunnel/constants/defaultContractCnpjOptions.constant"
import { PublicCompanyLeadsFunnelUpdateContractValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { ContractData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { InfoIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import useDebounce from '@/common/hooks/useDebounce';
import { useGeosOptions } from '@/common/hooks/queries/useGeosOptions';
import { filterOptions } from '@/features/users/constants/filterOptions.constant';
import { GeoTransformers } from '@/common/services/geoTransformers';
import { FilterOptionOption } from 'react-select';
import MaskedTextField from '@/common/components/MaskedTextField'

interface ContractInvoiceDetailsProps {
  contract?: ContractData
  submittableOnBlur: (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => void
  submit: () => void
  disableFields?: boolean
}

export const ContractInvoiceDetails = ({
  contract,
  submittableOnBlur,
  submit,
  disableFields,
}: ContractInvoiceDetailsProps) => {
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateContractValidationSchema>()

  const watchCycle = watch('contract.cycle.value')

  const getCycleStartDayDefaultValue = useMemo(() => {
    return contract?.cycle === 'weekly'
      ? contract?.cycle_start_day !== null && contract?.cycle_start_day !== undefined
        ? weekDaysOptions[contract?.cycle_start_day]
        : ''
      : monthDaysOptions.find(({ value }) => value === `${contract?.cycle_start_day}`)
  }, [contract?.cycle])

  const [geoSearch, setGeoSearch] = useDebounce('');

  const geos = useGeosOptions(geoSearch);

  useEffect(() => {
    if (contract?.cycle !== 'fixed' && watchCycle && watchCycle === 'fixed') {
      setValue('contract.cycle_fixed_interval', undefined)
    }
  }, [watchCycle])

  return (
    <Accordion
      className="py-1"
      name='contract_invoice_details'
      contentClassName="data-[state=open]:overflow-visible"
      head={<p>Detalhes da fatura <span className="text-danger-soft"> *</span></p>}
      body={
        <div className="flex flex-col gap-4">
          <SelectController
            control={control}
            name="contract.payment_modality"
            label="Modalidade de pagamento"
            value={expectedPaymentModalityOptions.find(({ value }) => value === contract?.payment_modality)}
            options={expectedPaymentModalityOptions}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
          <SelectController
            control={control}
            name="contract.duration_in_months"
            label="Vigência do contrato"
            errorMessage={errors?.contract?.duration_in_months?.message}
            defaultValue={durationInMonthsOptions.find(({ value }) => value === `${contract?.duration_in_months}`)}
            options={durationInMonthsOptions}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
          <SelectController
            control={control}
            name="contract.cycle"
            label="Ciclo de fatura"
            errorMessage={errors?.contract?.cycle?.message}
            defaultValue={cycleOptions.find(({ value }) => value === contract?.cycle)}
            options={cycleOptions}
            triggerOnChange={() => {
              setValue('contract.cycle_start_day', null)
              submit()
            }}
            isDisabled={disableFields}
            markAsRequired
          />
          {(watchCycle === 'fixed' || (contract?.cycle === 'fixed' && watchCycle === undefined)) && (
            <MaskedTextField
              label="Intervalo de ciclo fixo"
              placeholder="Quantidade em dias"
              mask='0000'
              className="w-full"
              {...register('contract.cycle_fixed_interval')}
              disabled={disableFields}
              errorMessage={errors?.contract?.cycle_fixed_interval?.message || undefined}
              defaultValue={contract?.cycle_fixed_interval ?? ''}
              onBlur={() => submit()}
            />
          )}
          <div className="flex gap-2">
            <SelectController
              control={control}
              name="contract.cycle_start_day"
              label="Dia de abertura"
              className="w-1/2"
              errorMessage={errors?.contract?.cycle_start_day?.message}
              defaultValue={getCycleStartDayDefaultValue}
              options={(watchCycle || contract?.cycle) === 'weekly' ? weekDaysOptions : monthDaysOptions(28)}
              triggerOnChange={submit}
              isDisabled={disableFields}
              markAsRequired
            />
            <SelectController
              control={control}
              name="contract.closing_to_due_in_days"
              label="Vencimento da fatura"
              className="w-1/2"
              errorMessage={errors?.contract?.closing_to_due_in_days?.message}
              defaultValue={
                monthDaysOptions(90).find(({ value }) => value === `${contract?.closing_to_due_in_days}`)
                  ? {
                    label: `D+${monthDaysOptions(90).find(({ value }) => value === `${contract?.closing_to_due_in_days}`)?.label
                      }`,
                    value: contract?.closing_to_due_in_days,
                  }
                  : null
              }
              options={monthDaysOptions(90).map((day) => ({ label: `D+${day.label}`, value: day.value }))}
              triggerOnChange={submit}
              isDisabled={disableFields}
              markAsRequired
            />
          </div>
          <SelectController
            control={control}
            name="contract.due_to_suspension_in_days"
            label="Dias após vencimento até inadimplência"
            errorMessage={errors?.contract?.due_to_suspension_in_days?.message}
            defaultValue={
              monthDaysOptions(90).find(({ value }) => value === `${contract?.due_to_suspension_in_days}`)
                ? {
                  label: `D+${monthDaysOptions(90).find(({ value }) => value === `${contract?.due_to_suspension_in_days}`)?.label
                    }`,
                  value: contract?.due_to_suspension_in_days,
                }
                : null
            }
            options={monthDaysOptions(90).map((day) => ({ label: `D+${day.label}`, value: day.value }))}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
          <div className="flex items-center gap-2">
            <InfoIcon strokeWidth={1} className="text-warning" />
            <div>
              <p className="font-bold text-[10px] leading-3">
                Uma nova fatura será aberta no dia posterior ao fechamento.
              </p>
              <p className="font-light text-[10px] leading-3 italic">
                Caso o dia selecionado não exista em um determinado mês (como o dia 31 em fevereiro),
                <br />o fechamento será ajustado automaticamente para o último dia desse mês.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <SelectController
              control={control}
              name="contract.bill_grouping_mode"
              label="Modo de agrupamento da Fatura"
              className="w-1/2"
              errorMessage={errors?.contract?.bill_grouping_mode?.message}
              defaultValue={billGroupingModeOptions.find(({ value }) => value === contract?.bill_grouping_mode)}
              options={billGroupingModeOptions.filter((option) => !option.company_kind || option.company_kind === 'public_company')}
              triggerOnChange={submit}
              isDisabled={disableFields}
              markAsRequired
            />
            <SelectController
              control={control}
              name="contract.bill_by"
              label="Segmentação da Fatura"
              className="w-1/2"
              errorMessage={errors?.contract?.bill_by?.message}
              defaultValue={billByOptions.find(({ value }) => value === contract?.bill_by)}
              options={billByOptions}
              triggerOnChange={submit}
              isDisabled={disableFields}
              markAsRequired
            />
          </div>
          <SelectController
            control={control}
            name="contract.fiscal_document_requirement"
            label="Tipo de recolha"
            errorMessage={errors?.contract?.fiscal_document_requirement?.message}
            defaultValue={fiscalDocumentRequirement.find(
              ({ value }) => value === contract?.fiscal_document_requirement,
            )}
            options={fiscalDocumentRequirement}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
          <SelectController
            control={control}
            name="contract.fiscal_document_consumer"
            label="CNPJ p/ coleta de nota fiscal (nota emitida pelo credenciado)"
            errorMessage={errors?.contract?.fiscal_document_consumer?.message}
            defaultValue={fiscalDocumentConsumerOptions.find(
              ({ value }) => value === contract?.fiscal_document_consumer,
            )}
            options={fiscalDocumentConsumerOptions}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
          <SelectController
            control={control}
            name="contract.cnpj_for_fiscal_document"
            label="CNPJ/CPF Padrão para emissão de NF"
            defaultValue={defaultContractCnpjOptions.find(({ value }) => value === contract?.cnpj_for_fiscal_document)}
            options={defaultContractCnpjOptions}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
          <Controller
            control={control}
            name="contract.global_value"
            defaultValue={contract?.global_value ? formatCurrency(Number(contract?.global_value)) : undefined}
            disabled={disableFields}
            render={({ field: { onBlur, ...rest } }) => (
              <CurrencyTextField
                label="Valor global do contrato"
                markAsRequired
                maxValue={9_999_999_999.99}
                {...rest}
                errorMessage={errors?.contract?.global_value?.message}
                onBlur={(e) => submittableOnBlur(e, onBlur)}
              />
            )}
          />
          <SelectController
            control={control}
            name="contract.payment_method"
            label="Método de pagamento"
            defaultValue={paymentMethodOptions.find(({ value }) => value === contract?.payment_method)}
            options={paymentMethodOptions}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />

          <SelectController
            control={control}
            name="contract.geo_geoables"
            label="Região de atuação"
            filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
            defaultValue={GeoTransformers.mapToGeo()(contract?.geo_geoables)}
            options={GeoTransformers.mapToGeoOptions()(geos)}
            errorMessage={errors?.contract?.geo_geoables?.message || undefined}
            isMulti
            onInputChange={setGeoSearch}
            triggerOnChange={submit}
            isDisabled={disableFields}
            markAsRequired
          />
        </div>
      }
    />
  )
}
